import { type SubmissionResult } from '@conform-to/react'
import {
	type ActionFunctionArgs,
	type LoaderFunctionArgs,
} from '@remix-run/node'
import {
	useFetcher,
	type ClientActionFunctionArgs,
	type ClientLoaderFunctionArgs,
	type FetcherWithComponents,
} from '@remix-run/react'
import { useEffect, useRef } from 'react'

export type FetcherData = {
	result: SubmissionResult<string[]>
}

export const fetcherSuccess = (fetcher: FetcherWithComponents<FetcherData>) => {
	return fetcher.state === 'idle' && fetcher.data?.result.status === 'success'
}

export const fetcherResourceSuccess = <T,>(
	fetcher: FetcherWithComponents<T>,
) => {
	return fetcher.state === 'idle' && fetcher.data !== undefined
}

export const fetcherError = (fetcher: FetcherWithComponents<FetcherData>) => {
	return fetcher.state === 'idle' && fetcher.data?.result.status === 'error'
}

export const fetcherResourceError = <T,>(fetcher: FetcherWithComponents<T>) => {
	return fetcher.state === 'idle' && fetcher.data === undefined
}

export const fetcherCompleted = (
	fetcher: FetcherWithComponents<FetcherData>,
) => {
	return fetcher.state === 'idle' && fetcher.data
}

export const fetcherFieldErrorMessages = (
	fetcher: FetcherWithComponents<FetcherData>,
): Record<string, string[] | null> => {
	const result = fetcher.data?.result
	if (!result?.error) return {}

	const fields: string[] = result.fields ?? []
	const errors: Record<string, string[] | null> = {}

	for (const field of fields) {
		const fieldErrors = result.error[field] ?? null
		if (fieldErrors) {
			errors[field] = fieldErrors
		}
	}

	return errors
}

export const fetcherErrorMessages = (
	fetcher: FetcherWithComponents<FetcherData>,
): string[] => {
	const result = fetcher.data?.result
	if (!result?.error) return []

	const fields: string[] = result.fields ?? []
	const errors: string[] = []

	// Include global errors first
	if (result.error._global) {
		errors.push(...result.error._global)
	}

	for (const field of fields) {
		const fieldErrors = result.error[field] ?? null
		if (fieldErrors) {
			errors.push(...fieldErrors)
		}
	}

	return errors
}

export async function waitForFetcher(fetcher: {
	state: 'idle' | 'submitting' | 'loading'
}) {
	return new Promise<void>((resolve) => {
		const check = () => {
			if (fetcher.state === 'idle') {
				resolve()
			} else {
				requestAnimationFrame(check)
			}
		}
		check()
	})
}

// --- Gist Integration Additions ---
// https://gist.github.com/rphlmr/637e939315127779c59fb67297a816d3
// Helper types and hook for standardized fetcher responses, enabling
// async/await patterns with fetcher submissions by returning a promise.
export function useAsyncFetcher<T extends LoaderOrActionFunction>() {
	const fetcher = useFetcher<T>()
	const deferred = useRef<Deferred<T> | null>(null)

	useEffect(() => {
		if (fetcher.state !== 'idle' || !fetcher.data || !deferred.current) {
			return
		}

		const response = fetcher.data

		if (response.error) {
			return deferred.current.reject(response.error)
		}

		return deferred.current.resolve(response.data)
	}, [fetcher.data, fetcher.state])

	return {
		submit: (...args: Parameters<typeof fetcher.submit>) => {
			fetcher.submit(...args)
			deferred.current = newDeferred()
			return deferred.current.promise
		},
	}
}

export type Deferred<T extends LoaderOrActionFunction> = ReturnType<
	typeof newDeferred<T>
>

export function newDeferred<T extends LoaderOrActionFunction>() {
	let resolve: any
	let reject: any

	const promise = new Promise<
		Extract<Awaited<ReturnType<T>>, DataResponse<unknown>>['data']
	>((_resolve, _reject) => {
		resolve = _resolve
		reject = _reject
	})

	return { resolve, reject, promise }
}

export type DataResponse<T> = ReturnType<typeof data<T>>

export function data<T>(data: T) {
	return {
		data,
		error: null,
	}
}

export type ErrorResponse = ReturnType<typeof error>

export function error(message: string) {
	return {
		data: null,
		error: {
			message,
		},
	}
}

type LoaderOrActionResponse<T> = DataResponse<T> | ErrorResponse

type ActionFunction = (
	args: ActionFunctionArgs,
) => Promise<LoaderOrActionResponse<unknown>>
type ClientActionFunction = (
	args: ClientActionFunctionArgs,
) => Promise<LoaderOrActionResponse<unknown>>
type LoaderFunction = (
	args: LoaderFunctionArgs,
) => Promise<LoaderOrActionResponse<unknown>>
type ClientLoaderFunction = (
	args: ClientLoaderFunctionArgs,
) => Promise<LoaderOrActionResponse<unknown>>
type LoaderOrActionFunction =
	| LoaderFunction
	| ClientLoaderFunction
	| ActionFunction
	| ClientActionFunction
